import { CrudType, HomeNavMenuType } from '@redux/index'

export interface MenuType {
	id: string
	name: string
	order?: number
	icon?: string
	to?: string
	children?: MenuType[]
	isDev?: boolean
	showMenu?: boolean
}

export interface SidePageType {
	id: string
	name: string
	to?: string
	targetCrud: CrudType
	parentPath: string
}

export const defaultMenu: MenuType = {
	id: 'home',
	name: 'Home',
	icon: '',
	to: '/home',
}

/**
 * Forum 은 사이트 밖이다 — Discourse 자체 호스팅 (FORUM_PLAN §14). 같은 탭으로 넘어간다
 * (SITEMAP_PLAN §5). 메뉴·푸터·라우트가 전부 이 상수 하나를 본다.
 */
export const FORUM_URL = 'https://forum.judahs.io'

/** 메뉴 uri 가 사이트 밖 주소인지. 밖이면 `navigate()` 가 아니라 주소창을 바꿔야 한다. */
export const isExternalUri = (uri: string): boolean => /^https?:\/\//.test(uri)

/**
 * 2026-09-09 사이트맵 §9 (judahsWeb/SITEMAP_PLAN.md).
 * Products(라인업 보기) 와 Shop(구매) 을 분리하고, Forum·About 을 올렸다. Home 은 로고가 대신한다.
 * 드롭다운은 없다 — 하위 메뉴는 모바일 드로어와 각 페이지의 인덱스 행으로만 존재한다.
 */
export const homeMenuList: HomeNavMenuType[] = [
	{ id: 'products', title: 'PRODUCTS', uri: '/products' },
	{ id: 'shop', title: 'SHOP', uri: '/shop' },
	{ id: 'support', title: 'SUPPORT', uri: '/support' },
	// 2026-09-10 포럼 오픈 — 빈 페이지 `/forum` 을 걷어내고 forum.judahs.io 로 직접 보낸다.
	{ id: 'forum', title: 'FORUM', uri: FORUM_URL },
	{ id: 'about', title: 'ABOUT', uri: '/about' },
	{ id: 'contact', title: 'CONTACT', uri: '/contact' },
]

/**
 * Products 하위 = 라인. 라벨은 라인 이름(Koinos · Kedem), 코드(PLS · MCS)는 페이지 키커에만 (SITEMAP_PLAN §8.2).
 * 라인 페이지는 두지 않고 `/products` 안의 앵커로 간다.
 */
export const productSubMenuList: HomeNavMenuType[] = [
	{ id: 'koinos', title: 'Koinos', uri: '/products#koinos' },
	{ id: 'kedem', title: 'Kedem', uri: '/products#kedem' },
	{ id: 'utility', title: 'Utility', uri: '/products#utility' },
]

/**
 * 계정 메뉴. 2026-09-26 역할 항목 둘을 보탰다 (judahsWeb/PARTNER_PLAN.md §2).
 * 아바타 드롭다운은 만들지 않는다 — 계정 페이지 안의 가로 네비(MonolithAccountNav)와
 * 모바일 드로어가 이 배열을 `visibleCustomerMenu(roles)` 로 걸러 그린다.
 *   Wholesale Orders  DEALER 또는 DISTRIBUTOR
 *   Performance       INFLUENCER
 * 역할 코드는 DB `CUSTOMER_ROLE.CODE` (customerRole.config.ts) 와 같다.
 */
export const customerSubMenuList: HomeNavMenuType[] = [
	{ id: 'myInfo', title: 'My Info', uri: '/customer/info' },
	{ id: 'myOrder', title: 'My Order', uri: '/customer/order' },
	// 2026-10-06 Contact 에 남긴 글과 답변 (judahsWeb/INQUIRY_THREAD_PLAN.md). 대화 한 건 = /customer/inquiry/:id
	// 같은 날 My Messages(/customer/messages) 에서 바꿨다 — "Messages" 는 주문 알림까지 모이는 받은편지함으로 읽힌다
	{ id: 'myInquiries', title: 'My Inquiries', uri: '/customer/inquiry' },
	{
		id: 'wholesale',
		title: 'Wholesale Orders',
		uri: '/customer/wholesale',
		roles: ['DEALER', 'DISTRIBUTOR'],
	},
	{
		id: 'performance',
		title: 'Performance',
		uri: '/customer/performance',
		roles: ['INFLUENCER'],
	},
	// Change Password · Log out 은 2026-10-06 My Info 안의 버튼으로 옮겼다(사용자 지시). 옛 주소는 라우트가 넘긴다
]

/** 역할이 필요한 항목은 그 역할이 있을 때만 남긴다. `roles` 가 없는 항목은 늘 남는다 */
export const visibleCustomerMenu = (customerRoles: string[] = []): HomeNavMenuType[] =>
	customerSubMenuList.filter(
		aMenu =>
			aMenu.roles === undefined ||
			aMenu.roles.some(aRole => customerRoles.includes(aRole)),
	)

/** 경로 → 필요한 역할. 가드(CustomerLoginGuard)가 본다. 표에 없는 경로는 로그인만 필요하다 */
export const customerRoleRequiredByPath: { [path: string]: string[] } =
	customerSubMenuList
		.filter(aMenu => aMenu.roles !== undefined)
		.reduce((acc, aMenu) => ({ ...acc, [aMenu.uri]: aMenu.roles as string[] }), {})

/**
 * 경로의 필요 역할 — 하위 주소도 본다(`/customer/wholesale/:orderId` 는 `/customer/wholesale` 의 역할).
 * 없으면 undefined(로그인만 필요).
 */
export const customerRoleRequiredFor = (path: string): string[] | undefined => {
	const key = Object.keys(customerRoleRequiredByPath).find(
		aUri => path === aUri || path.startsWith(`${aUri}/`),
	)
	return key === undefined ? undefined : customerRoleRequiredByPath[key]
}

// 2026-09-08: Privacy / Store Policy 는 지원 메뉴에서 제거(푸터에만 둔다), Software 추가 (PENDING_CHANGES §12)
// 2026-09-09: Works with · FAQ 추가. Contact 은 GNB 에만 있고 여기에는 없다 (SITEMAP_PLAN §7.1 — 한 페이지에 자리 하나)
export const supportSubMenuList: HomeNavMenuType[] = [
	{ id: 'manuals', title: 'Manuals', uri: '/manuals' },
	{ id: 'software', title: 'Software', uri: '/software' },
	{ id: 'worksWith', title: 'Works with', uri: '/works-with' },
	{ id: 'faq', title: 'FAQ', uri: '/faq' },
]

export const mobileMenuList = []

// id 앞에 prefix로 'admin'을 붙일 것
export const adminMenu: MenuType[] = [
	{
		id: 'adminUsers',
		name: '사용자 관리',
		icon: 'account-cog',
		to: '/admin/users',
	},
	{
		id: 'adminMenu',
		name: '메뉴 관리',
		icon: 'list-box',
		to: '/admin/menu',
	},
	{
		id: 'adminAccessControl',
		name: '권한 관리',
		icon: 'badge-account-outline',
		to: '/admin/access-control',
	},
	{
		id: 'adminLogs',
		name: 'API 로그',
		icon: 'file-document-outline',
		to: '/admin/logs',
		children: [
			{
				id: 'adminLogsDashboard',
				name: '대시보드',
				icon: 'chart-box-outline',
				to: '/admin/logs',
			},
			{
				id: 'adminLogsList',
				name: '상세 목록',
				icon: 'format-list-bulleted',
				to: '/admin/logs/list',
			},
			{
				id: 'adminLogsSettings',
				name: '이메일 설정',
				icon: 'email-outline',
				to: '/admin/logs/settings',
			},
		],
	},
]

export const sidePageList: SidePageType[] = [
	{
		id: 'resetPassword',
		name: '패스워드 변경',
		to: '/admin/reset-password',
		targetCrud: ['C', 'U'],
		parentPath: '/admin/users',
	},
	{
		id: 'userProfileImageUpdate',
		name: '프로필 이미지 변경',
		to: '/admin/user-profile-image-update',
		targetCrud: ['C', 'U'],
		parentPath: '/admin/users',
	},
	{
		id: 'createMenu',
		name: '새 메뉴 생성',
		to: '/admin/create-menu',
		targetCrud: ['C'],
		parentPath: '/admin/menu',
	},
	// main
	{
		id: 'resetCustomerPassword',
		name: '고객 비밀번호 변경',
		to: '/main/reset-customer-password',
		targetCrud: ['C', 'U'],
		parentPath: '/main/customer',
	},
	{
		id: 'inquiryDetail',
		name: '문의 상세',
		to: '/main/inquiry/:inquiryId',
		targetCrud: ['R'],
		parentPath: '/main/inquiry',
	},
	{
		// 주문 상세 — 자기 URL 을 가진 전체 화면. 권한은 부모인 /main/order 를 따른다.
		id: 'orderDetail',
		name: '주문 상세',
		to: '/main/order/:orderId',
		targetCrud: ['R'],
		parentPath: '/main/order',
	},
	{
		// 배송 건 추가(수동) — 부모 메뉴의 생성 권한을 따른다 (DISPATCH_PLAN §3.7)
		id: 'dispatchCreate',
		name: '배송 건 추가',
		to: '/main/shipping/new',
		targetCrud: ['C'],
		parentPath: '/main/shipping',
	},
	{
		// 배송 건 자세히 — 자기 URL 을 가진 전체 화면. 권한은 부모인 /main/shipping 을 따른다.
		id: 'shipmentDetail',
		name: '배송 건 자세히',
		to: '/main/shipping/:dispatchId',
		targetCrud: ['R'],
		parentPath: '/main/shipping',
	},
	{
		// 라벨 만들기 — 누르면 FedEx 에 등록되고 요금이 나간다. 부모 메뉴의 생성 권한을 따른다.
		id: 'shipmentLabelCreate',
		name: '라벨 생성',
		to: '/main/shipping/:dispatchId/label/new',
		targetCrud: ['C'],
		parentPath: '/main/shipping',
	},
	{
		id: 'shipmentLabelView',
		name: '라벨 보기',
		to: '/main/shipping/:dispatchId/label',
		targetCrud: ['R'],
		parentPath: '/main/shipping',
	},
	{
		id: 'shipmentLabelViewOne',
		name: '라벨 보기(이력)',
		to: '/main/shipping/:dispatchId/label/:shipmentId',
		targetCrud: ['R'],
		parentPath: '/main/shipping',
	},
	{
		// 시즌 상품 상세 — 판매 단계와 이행 로드맵. 시즌 수정과 같이 전체 화면이고
		// 권한은 부모인 /main/season 을 따른다.
		id: 'seasonProductDetail',
		name: '시즌 상품 상세',
		to: '/main/season/:seasonId/product/:seasonProductId',
		targetCrud: ['U'],
		parentPath: '/main/season',
	},
]
